import {HedgePresetsModule} from '../hedge-presets/hedge-presets.module';
import {NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DevExtremeModule} from '../devextreme.module';
import {OptionsCommonModule} from '../options-common/options-common.module';
import {AdjustmentPricingGridModule} from '../adjustment-pricing-grid/adjustment-pricing-grid.module';
import {HedgingGridModule} from '../hedging-grid/hedging-grid.module';
import {CopilotSharedModule} from '../copilot-shared/copilot-shared.module';

import {MavenPanelComponent} from './maven-panel.component';
import {MavenTemplateSelectorComponent} from './templates/maven-template-selector.component';
import {MavenOverrideIndicatorsComponent} from './setup/maven-override-indicators.component';
import {MavenResultsComponent} from './results/maven-results.component';
import {MavenCardComponent} from './results/maven-card.component';
import {MavenDetailsComponent} from './details/maven-details.component';
import {MavenSetupComponent} from './setup/maven-setup.component';
import {MavenSideParametersComponent} from './setup/maven-side-parameters.component';
import {MavenCloseSelectionComponent} from './setup/maven-close-selection.component';
import {MAVEN_RUN_SKILL} from './run-client/maven-run-skill';
import {MavenRunClient} from './run-client/maven-run-client';
// Imported and not referenced, deliberately — the same thing AdjustmentPricingGridModule does
// with MockRollPositionsSkill. It keeps the DI alternative a genuinely ONE-LINE flip, and it
// keeps the stub inside the type gate: an unreferenced file is an unchecked file, and a stub that
// stopped compiling would be discovered at the moment someone needed it.
// tslint:disable-next-line: no-unused-variable
import {MavenRunClientStub} from './run-client/maven-run-client.stub';

import {CashFlowPortfoliosService} from '../adjustment-pricing-grid/services/cashflow-portfolios.service';
import {
    CashFlowStrategyTemplatesService
} from '../adjustment-pricing-grid/services/cashflow-strategy-templates.service';
import {SavedPositionsService} from '../adjustment-pricing-grid/services/saved-positions.service';
import {PositionsStateService} from '../adjustment-pricing-grid/services/positions-before-state.service';
import {ApgDataService} from '../adjustment-pricing-grid/services/apg-data.service';

// THE MAVEN FEATURE MODULE (CR-20260910-001 milestone 4).
//
// ⚠ OWNED BY LANE S6, TOGETHER WITH THE PANEL TEMPLATE (build plan section 6, contention
// register). Lane S7 fills maven/results/ and maven/details/ and REQUESTS any addition to this
// file from S6 through the leader. No lane-to-lane messages, and no working around this.
//
// WHY THE TWO CHILD COMPONENTS ARE ALREADY HERE. They are empty shells that render nothing (see
// their own headers). Registering them from the first commit is what lets S7 fill the bodies
// without ever opening this file: their selectors are the seam that S6's template binds to. The
// alternative — placeholder TAGS with no registered component — would either throw "not a known
// element" at the headless boot gate, or force CUSTOM_ELEMENTS_SCHEMA onto this module, which
// suppresses that error for EVERY unknown tag here, forever. A gate that cannot see a typo is not
// a gate. So: real components, empty bodies, one owner each.
//
// PROVIDERS — WHAT IS HERE, AND WHAT IS DELIBERATELY STILL ABSENT
//
// Assessment item 30: several of the APG's services are @Injectable() WITHOUT providedIn and
// ASSERT on an unset ServiceConfiguration{userId} the moment they build a storage key
// (copy-orders-to.service.ts:496, positions-after-state.service.ts:47,154). Every panel module in
// this app is eager (workspace.module.ts), so NgModule providers hoist to the ROOT injector —
// which means a provider added here WITHOUT a configure() call does not merely sit unused: Maven
// would resolve an instance ANOTHER panel configured, or one nobody configured at all. That is
// worse than no provider. So a service arrives here only WITH the code that configures it.
//
// The four below are configured. CashFlowPortfoliosService is configured by the Setup component
// at init — once per user whose portfolios it reads, which for a super user is several. The other
// three are configured BY ApgDataService itself, on every call, with the PORTFOLIO'S OWNER
// (apg-data.service.ts:98-104, :152-157) — which is exactly ruling R-E: a super user may run on
// another trader's portfolio, and every saved-state read follows the portfolio, not the login.
//
// ⚠ HedgePositionsService WAS here for the "Existing Hedges" saved-book read-out, which Alan
// removed at G3 ("he does not want the hedges shown, only the choice"). Its last consumer in this
// module is gone, so the explicit provider is gone with it — a provider whose stated reason no
// longer exists is the stale-justification trap, and the next reader cannot tell a live one from
// a leftover. It remains resolvable regardless: HedgingGridModule provides it and is imported
// below, and it takes no ServiceConfiguration, so nothing needed configuring either way.
//
// STILL ABSENT, and each lands with the unit that uses it:
//   - CopyOrdersToService         -> the Orders sub-section (lane S7 requests it)
//   - PositionsAfterStateService  -> the Orders sub-section (lane S7 requests it)
//   - LandmineDaysService         -> the Date Range section's landmine marking (blocked on Q1)
// Each arrival brings a TestBed resolution spec that resolves it THROUGH THE INJECTOR, per the
// lane's gate — `new` does not build the factory that throws.
@NgModule({
    imports: [HedgePresetsModule,
        CommonModule,
        DevExtremeModule,
        // For ets-strikes-dropdown: the Range coverage's end strike is picked from the platform's
        // own strike picker, never typed into a plain number box.
        OptionsCommonModule,
        // For <ets-apg-roll-positions-panel> — the APG ORION's date questionnaire, exported for
        // reuse by lane S2 on leader ruling L21. Maven hosts it unchanged, which is what keeps
        // Maven's date rules identical to the APG's by construction rather than by intention.
        // It also exports AdjustmentSolutionPopupComponent, which is the Details column's
        // adjustment order.
        AdjustmentPricingGridModule,

        // For <ets-hg-transaction-popup> — the Details column's hedge order. HedgingGridModule is
        // the only module that exports HgTransactionPopupComponent, so without this the tag is an
        // unknown element at the boot gate.
        //
        // ⚠ THE PROVIDER QUESTION THIS RAISES, ANSWERED BEFORE COMMITTING (the header above warns
        // that an eagerly-imported module's providers hoist to the ROOT injector, so importing one
        // for its components drags its provider set along on the same terms).
        //
        // It adds NOTHING to the root injector. `WorkspaceModule` ALREADY imports HedgingGridModule
        // eagerly (workspace.module.ts:91), and WorkspaceModule is itself eagerly imported by
        // HostModule — so all fourteen of its providers are in the root injector today, before this
        // line. This import makes its exported COMPONENTS visible to Maven's templates and changes
        // the injector not at all.
        //
        // Six of those fourteen are services Maven also provides (the templates, portfolios, saved
        // positions, hedge positions, APG data and positions-state services). That duplication is
        // pre-existing for the same reason, and Maven's own reads survive it by construction:
        // ApgDataService configures its dependencies with the portfolio's owner on EVERY call, and
        // MavenSetupComponent configures CashFlowPortfoliosService immediately before each
        // getPortfolios() in the same synchronous block — so a configure from another panel cannot
        // interleave between the two.
        HedgingGridModule,

        // CR-20260915-001: for <ets-opg-override-indicators> — the ORION (OPG)'s Override
        // Indicators fieldset, hosted unchanged under "Override Market Indicators". Same provider
        // question, same answer: its one provider (OPG_TRADES_SKILL) is already in the root
        // injector through the eagerly-imported OPG and Hedging Grid modules, so this import makes
        // the fieldset visible to Maven's templates and changes the injector not at all.
        CopilotSharedModule
    ],
    declarations: [
        MavenTemplateSelectorComponent,
        MavenPanelComponent,
        MavenSetupComponent,
        MavenSideParametersComponent,
        // CR-20260916-003: the Close selection under the Existing Hedges buttons — the book's
        // expirations and hedges with live prices from the platform's own HedgesPricingService
        // (HedgePositionsService resolves through the eagerly-imported HedgingGridModule, as the
        // header above records; nothing new is provided here).
        MavenCloseSelectionComponent,
        // CR-20260915-001: the host of the override fieldset(s) — owns the readings feed and the
        // date stepping for the Setup's "Override Market Indicators" section.
        MavenOverrideIndicatorsComponent,
        MavenResultsComponent,

        // ⚠ THE CARD ITSELF. Undeclared, <ets-maven-card> is an unknown element: the *ngFor in the
        // Results column still runs once per card and Angular renders NOTHING for each one. The
        // sort bar above it is plain markup, so it appeared - which is exactly how this presented
        // at the smoke: stages ran, the toolbar arrived, and five delivered cards were invisible.
        // Nothing fails, nothing throws where a trader can see it, and every other check passes.
        MavenCardComponent,

        MavenDetailsComponent
    ],
    exports: [
        MavenPanelComponent
    ],
    providers: [
        // THE RUN SEAM. MavenRunClient is the default and MavenRunClientStub is the DI
        // alternative — flip this one line to drive the whole panel from memory while milestone
        // 3's engine is still being written. The stub stays in the tree deliberately; it is how
        // this lane builds and gates without a backend, not a test fixture.
        //
        // Module-level, so ONE client serves every Maven panel instance: its pending-run map is
        // keyed by runId, and supersede is scoped per panelInstanceId server-side.
        //
        // Neither implementation takes a ServiceConfiguration, so neither has the configure()
        // hazard described above: MavenRunClient's only dependencies are the message bus and the
        // shell client, both root-provided, and the stub has none at all.
        {provide: MAVEN_RUN_SKILL, useClass: MavenRunClient},

        // Configured by MavenSetupComponent at init, per user whose portfolios it reads.
        CashFlowPortfoliosService,

        // Configured by ApgDataService on every call, with the portfolio's OWNER. The templates
        // service is also asked directly by the Setup (to list own + assigned templates), and the
        // Setup configures it for the same owner before it asks.
        CashFlowStrategyTemplatesService,
        SavedPositionsService,
        PositionsStateService,
        ApgDataService
    ]
})
export class MavenModule {
}
